import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  ChevronRightIcon,
  ViewColumnsIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  TagIcon,
  ShoppingBagIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import Breadcrumbs from '../components/categories/Breadcrumbs';

const CategoriesPage = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedParent, setSelectedParent] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get('/api/categories', { headers });
      
      setCategories(response.data.allCategories || []);
    } catch (error) {
      toast.error('שגיאה בטעינת קטגוריות');
    } finally {
      setLoading(false);
    }
  };

  const filterCategories = (categories) => {
    return categories.filter(category => {
      // Filter by search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const nameMatch = category.name?.toLowerCase().includes(query);
        const nameEnMatch = category.name_en?.toLowerCase().includes(query);
        const descMatch = category.description?.toLowerCase().includes(query);
        
        if (!nameMatch && !nameEnMatch && !descMatch) {
          return false;
        }
      }

      // Filter by parent
      if (selectedParent) {
        return category.parent_id === selectedParent;
      }

      // Only show active categories to non-admin users
      if (!user || user.role !== 'admin') {
        if (!category.is_active) {
          return false;
        }

        // Check visibility settings
        const visibility = category.visibility_settings || { audience: 'both' };
        if (visibility.audience === 'b2b' && user?.role !== 'b2b') {
          return false;
        }
        if (visibility.audience === 'b2c' && user?.role === 'b2b') {
          return false;
        }
      }

      return true;
    });
  };

  const rootCategories = filterCategories(
    categories.filter(cat => !cat.parent_id || selectedParent)
  );

  const getSubcategories = (parentId) => {
    return categories.filter(cat => cat.parent_id === parentId && cat.is_active);
  };

  const CategoryCard = ({ category }) => {
    const subcategories = getSubcategories(category.id);
    
    return (
      <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
        {category.image && (
          <div className="h-40 sm:h-48 lg:h-52 overflow-hidden bg-gray-200">
            <img
              src={category.image}
              alt={category.name}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
            />
          </div>
        )}
        
        <div className="p-4 sm:p-5 lg:p-6">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">
              {category.name}
            </h3>
            {category.is_featured && (
              <span className="px-2 py-0.5 sm:py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full flex-shrink-0">
                מומלץ
              </span>
            )}
          </div>

          {category.name_en && (
            <p className="text-xs sm:text-sm text-gray-500 mb-1 sm:mb-2">{category.name_en}</p>
          )}

          {category.description && (
            <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 line-clamp-2">
              {category.description}
            </p>
          )}

          {/* Category Features - Mobile Optimized */}
          <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
            {category.allow_negotiations && (
              <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                <TagIcon className="h-3 w-3 mr-0.5 sm:mr-1" />
                משא ומתן
              </span>
            )}
            {category.min_order_quantity > 1 && (
              <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                <ShoppingBagIcon className="h-3 w-3 mr-0.5 sm:mr-1" />
                מינימום {category.min_order_quantity} יח'
              </span>
            )}
            {category.default_warranty_period && (
              <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                אחריות {Math.floor(category.default_warranty_period / 30)} חודשים
              </span>
            )}
          </div>

          {/* Subcategories - Mobile Optimized */}
          {subcategories.length > 0 && (
            <div className="border-t pt-3 sm:pt-4 mt-3 sm:mt-4">
              <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">קטגוריות משנה:</h4>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {subcategories.slice(0, 5).map(subcat => (
                  <Link
                    key={subcat.id}
                    to={`/category/${subcat.slug}`}
                    className="text-xs px-2 py-0.5 sm:py-1 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-md text-gray-700 transition-colors touch-manipulation"
                  >
                    {subcat.name}
                  </Link>
                ))}
                {subcategories.length > 5 && (
                  <span className="text-xs text-gray-500">
                    +{subcategories.length - 5} נוספות
                  </span>
                )}
              </div>
            </div>
          )}

          <Link
            to={`/category/${category.slug}`}
            className="mt-3 sm:mt-4 w-full inline-flex items-center justify-center px-4 py-2.5 sm:py-2 text-sm sm:text-base bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white rounded-lg transition-colors touch-manipulation"
          >
            צפה במוצרים
            <ChevronRightIcon className="h-4 w-4 mr-2" />
          </Link>
        </div>
      </div>
    );
  };

  const CategoryListItem = ({ category }) => {
    const subcategories = getSubcategories(category.id);
    
    return (
      <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 p-4 sm:p-5 lg:p-6 mb-3 sm:mb-4">
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <div className="flex-1 w-full">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                {category.name}
              </h3>
              {category.is_featured && (
                <span className="px-2 py-0.5 sm:py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">
                  מומלץ
                </span>
              )}
              {category.allow_negotiations && (
                <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <TagIcon className="h-3 w-3 mr-0.5 sm:mr-1" />
                  משא ומתן
                </span>
              )}
            </div>

            {category.name_en && (
              <p className="text-xs sm:text-sm text-gray-500 mb-1 sm:mb-2">{category.name_en}</p>
            )}

            {category.description && (
              <p className="text-sm sm:text-base text-gray-600 mb-2 sm:mb-3">{category.description}</p>
            )}

            {/* Features - Mobile Optimized */}
            <div className="flex flex-wrap gap-2 sm:gap-3 text-xs sm:text-sm text-gray-600">
              {category.min_order_quantity > 1 && (
                <span>הזמנה מינימלית: {category.min_order_quantity} יחידות</span>
              )}
              {category.default_warranty_period && (
                <span>אחריות: {Math.floor(category.default_warranty_period / 30)} חודשים</span>
              )}
              {category.allow_reviews && (
                <span>ניתן להוסיף ביקורות</span>
              )}
            </div>

            {/* Subcategories - Mobile Optimized */}
            {subcategories.length > 0 && (
              <div className="mt-3 sm:mt-4">
                <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">קטגוריות משנה:</h4>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {subcategories.map(subcat => (
                    <Link
                      key={subcat.id}
                      to={`/category/${subcat.slug}`}
                      className="text-xs sm:text-sm px-2 sm:px-3 py-0.5 sm:py-1 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-md text-gray-700 transition-colors touch-manipulation"
                    >
                      {subcat.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex sm:flex-col items-center gap-3 w-full sm:w-auto">
            {category.image && (
              <img
                src={category.image}
                alt={category.name}
                className="w-20 h-20 sm:w-28 sm:h-28 lg:w-32 lg:h-32 object-cover rounded-lg flex-shrink-0"
              />
            )}
            <Link
              to={`/category/${category.slug}`}
              className="flex-1 sm:flex-initial w-full sm:w-auto sm:mt-3 lg:mt-4 inline-flex items-center justify-center px-4 py-2.5 sm:py-2 text-sm sm:text-base bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white rounded-lg transition-colors touch-manipulation"
            >
              צפה במוצרים
              <ChevronRightIcon className="h-4 w-4 mr-2" />
            </Link>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">טוען קטגוריות...</p>
        </div>
      </div>
    );
  }

  const breadcrumbsItems = [
    { name: 'קטגוריות', url: '/categories' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Mobile First */}
      <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 py-8 sm:py-12 lg:py-16 relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-48 h-48 sm:w-64 sm:h-64 bg-indigo-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-56 h-56 sm:w-72 sm:h-72 bg-purple-500 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          {/* Breadcrumbs - Hidden on small mobile */}
          <div className="mb-4 sm:mb-6 hidden sm:block">
            <Breadcrumbs items={breadcrumbsItems} />
          </div>
          
          <div className="text-center">
            <div className="inline-flex items-center justify-center mb-3 sm:mb-4">
              <TagIcon className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-indigo-600 ml-2 sm:ml-3" />
              <SparklesIcon className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 text-yellow-500" />
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-2 sm:mb-3 lg:mb-4">קטגוריות מוצרים</h1>
            <p className="text-sm sm:text-base lg:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              גלה את מגוון המוצרים הרחב שלנו לפי קטגוריות מותאמות אישית
            </p>
          </div>
        </div>
      </div>

      {/* Filters and Search - Mobile First */}
      <div className="container mx-auto px-4 py-4 sm:py-6">
        <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center justify-between">
            {/* Search */}
            <div className="flex-1 w-full">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="חיפוש קטגוריות..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pr-10 pl-4 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* View Mode Toggle - Better Mobile UX */}
            <div className="flex gap-2 w-full sm:w-auto">
              <button
                onClick={() => setViewMode('grid')}
                className={`flex-1 sm:flex-initial p-2.5 sm:p-2 rounded-lg transition-colors touch-manipulation ${
                  viewMode === 'grid'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300 active:bg-gray-400'
                }`}
                title="תצוגת רשת"
                aria-label="תצוגת רשת"
              >
                <ViewColumnsIcon className="h-5 w-5 mx-auto" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex-1 sm:flex-initial p-2.5 sm:p-2 rounded-lg transition-colors touch-manipulation ${
                  viewMode === 'list'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300 active:bg-gray-400'
                }`}
                title="תצוגת רשימה"
                aria-label="תצוגת רשימה"
              >
                <ListBulletIcon className="h-5 w-5 mx-auto" />
              </button>
            </div>
          </div>
        </div>

        {/* Breadcrumb - Mobile Optimized */}
        {selectedParent && (
          <div className="mb-4 sm:mb-6 flex items-center">
            <button
              onClick={() => setSelectedParent(null)}
              className="text-indigo-600 hover:text-indigo-700 active:text-indigo-800 text-xs sm:text-sm font-medium touch-manipulation"
            >
              כל הקטגוריות
            </button>
            <ChevronRightIcon className="h-4 w-4 mx-1.5 sm:mx-2 text-gray-400" />
            <span className="text-xs sm:text-sm text-gray-600">
              {categories.find(c => c.id === selectedParent)?.name}
            </span>
          </div>
        )}

        {/* Categories Grid/List - Mobile First */}
        {rootCategories.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-base sm:text-lg">לא נמצאו קטגוריות</p>
          </div>
        ) : (
          <div className={
            viewMode === 'grid'
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6'
              : 'space-y-3 sm:space-y-4'
          }>
            {rootCategories.map(category => (
              viewMode === 'grid' ? (
                <CategoryCard key={category.id} category={category} />
              ) : (
                <CategoryListItem key={category.id} category={category} />
              )
            ))}
          </div>
        )}
      </div>

      {/* Info Section - Mobile First */}
      <div className="container mx-auto px-4 py-8 sm:py-12">
        <div className="bg-indigo-50 rounded-lg p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">למה לבחור בנו?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            <div>
              <h3 className="font-semibold text-base sm:text-lg mb-1.5 sm:mb-2">מגוון רחב</h3>
              <p className="text-sm sm:text-base text-gray-600">מגוון קטגוריות ומוצרים לכל צורך</p>
            </div>
            <div>
              <h3 className="font-semibold text-base sm:text-lg mb-1.5 sm:mb-2">שקיפות מלאה</h3>
              <p className="text-sm sm:text-base text-gray-600">מידע מפורט על כל מוצר וקטגוריה</p>
            </div>
            <div>
              <h3 className="font-semibold text-base sm:text-lg mb-1.5 sm:mb-2">שירות מעולה</h3>
              <p className="text-sm sm:text-base text-gray-600">תמיכה מקצועית ומחירים תחרותיים</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoriesPage;
